import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDrag } from '../../hooks/useDrag';
import { Box } from '@mui/material';
import { getUserNotes, updateUserNotes } from '../../api/user';
import { useDispatch, useSelector } from 'react-redux';
import { setUserNotes } from '../../redux/reducers/common';
import { Minus, Pencil, X } from 'lucide-react';
import NotesEditor from './NotesEditor';
import './styles.css';

type SaveStatus = 'idle' | 'unsaved' | 'saving' | 'saved';

const DraggableNotes = () => {
  const draggableRef: any = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [size, setSize] = useState({ width: 340, height: 400 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [editorKey, setEditorKey] = useState(0);
  const latestHtmlRef = useRef<string>('');
  const dirtyRef = useRef(false);
  const skipInitialChangeRef = useRef(true);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dispatch: any = useDispatch();
  const notes = useSelector((state: any) => state.common.notes);
  const { position, handleMouseDown } = useDrag({ ref: draggableRef });

  const persistNotes = useCallback(
    async (html: string) => {
      setSaveStatus('saving');
      try {
        const response = await updateUserNotes({ userNotes: html });
      dispatch((setUserNotes as any)(response.userNotes));
        dirtyRef.current = false;
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus((s) => (s === 'saved' ? 'idle' : s)), 1800);
      } catch {
        setSaveStatus('unsaved');
      }
    },
    [dispatch],
  );

  const scheduleAutosave = useCallback(
    (html: string) => {
      latestHtmlRef.current = html;
      dirtyRef.current = true;
      setSaveStatus('unsaved');
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        persistNotes(html);
      }, 900);
    },
    [persistNotes],
  );

  const manageNotesClick = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (notes == null) {
      const response = await getUserNotes();
      dispatch((setUserNotes as any)(response.userNotes ?? ''));
      latestHtmlRef.current = response.userNotes ?? '';
    } else {
      latestHtmlRef.current = typeof notes === 'string' ? notes : '';
    }
    setEditorKey((k) => k + 1);
    skipInitialChangeRef.current = true;
    setIsOpen(true);
    setSaveStatus('idle');
  };

  const onMinimize = async () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (dirtyRef.current) {
      await persistNotes(latestHtmlRef.current);
    }
    setIsOpen(false);
  };

  const handleHtmlChange = (html: string) => {
    // Ignore Lexical's first onChange from initial content hydration
    if (skipInitialChangeRef.current) {
      skipInitialChangeRef.current = false;
      latestHtmlRef.current = html;
      return;
    }
    dispatch((setUserNotes as any)(html));
    scheduleAutosave(html);
  };

  const handleResizeStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height,
    });
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;
      setSize({
        width: Math.max(280, resizeStart.width + deltaX),
        height: Math.max(320, resizeStart.height + deltaY),
      });
    };

    const handleMouseUp = () => setIsResizing(false);

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeStart]);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const statusLabel =
    saveStatus === 'saving'
      ? 'Saving…'
      : saveStatus === 'saved'
        ? 'Saved'
        : saveStatus === 'unsaved'
          ? 'Unsaved'
          : '';

  const chipDragStart = useRef<{ x: number; y: number; event: React.MouseEvent } | null>(null);

  const handleChipMouseDown = (e: React.MouseEvent) => {
    // Defer drag until the pointer actually moves — keeps click = open
    chipDragStart.current = { x: e.clientX, y: e.clientY, event: e };
    const onMove = (ev: MouseEvent) => {
      if (!chipDragStart.current) return;
      const dx = ev.clientX - chipDragStart.current.x;
      const dy = ev.clientY - chipDragStart.current.y;
      if (Math.hypot(dx, dy) > 5) {
        handleMouseDown(chipDragStart.current.event);
        cleanup();
      }
    };
    const cleanup = () => {
      chipDragStart.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', cleanup);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', cleanup);
  };

  return (
    <div
      ref={draggableRef}
      style={{
        top: position.y,
        left: position.x,
        position: 'fixed',
        zIndex: 9999,
      }}
    >
      {isOpen ? (
        <div
          className="notesPanel"
          style={{ width: `${size.width}px`, height: `${size.height}px` }}
        >
          {/* Drag handle = header only */}
          <div className="notesPanel__header" onMouseDown={handleMouseDown}>
            <div className="notesPanel__titleWrap">
              <span className="notesPanel__accentDot" />
              <span className="notesPanel__title">My Notes</span>
              {statusLabel && (
                <span
                  className={`notesPanel__status${
                    saveStatus === 'saving'
                      ? ' is-saving'
                      : saveStatus === 'saved'
                        ? ' is-saved'
                        : ''
                  }`}
                >
                  {statusLabel}
                </span>
              )}
            </div>

            <div className="notesPanel__actions" onMouseDown={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="notesPanel__iconBtn"
                title="Minimize"
                onClick={onMinimize}
              >
                <Minus size={15} />
              </button>
              <button
                type="button"
                className="notesPanel__iconBtn"
                title="Close"
                onClick={onMinimize}
              >
                <X size={15} />
              </button>
            </div>
          </div>

          <Box
            sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <NotesEditor
              key={editorKey}
              initialHtml={typeof notes === 'string' ? notes : ''}
              onHtmlChange={handleHtmlChange}
            />
          </Box>

          <div className="notesPanel__resize" onMouseDown={handleResizeStart}>
            <svg width="16" height="16" viewBox="0 0 20 20">
              <path
                d="M 20 15 L 20 20 L 15 20 M 20 8 L 20 13 L 13 20 L 8 20"
                stroke="#7D8793"
                strokeWidth="1.5"
                fill="none"
              />
            </svg>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="notesChip"
          onClick={manageNotesClick}
          onMouseDown={handleChipMouseDown}
          title="Notes"
          aria-label="Open notes"
        >
          <span className="notesChip__icon">
            <Pencil size={16} strokeWidth={2.2} />
          </span>
          <span className="notesChip__label">Notes</span>
        </button>
      )}
    </div>
  );
};

export default DraggableNotes;
